import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate } from '../utils/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { TrendingUp, Target } from 'lucide-react';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProgressPage() {
  const [scores, setScores] = useState([]);
  const [todayScore, setTodayScore] = useState({
    lede_score: 0,
    nut_graf_score: 0,
    style_score: 0,
    fact_check_score: 0
  });
  const [saving, setSaving] = useState(false);
  
  const today = formatDate();

  useEffect(() => {
    fetchScores();
  }, []);

  const fetchScores = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/rubric/scores`);
      setScores(response.data);
      
      const todayScoreData = response.data.find(s => s.date === today);
      if (todayScoreData) {
        setTodayScore({
          lede_score: todayScoreData.lede_score,
          nut_graf_score: todayScoreData.nut_graf_score,
          style_score: todayScoreData.style_score,
          fact_check_score: todayScoreData.fact_check_score
        });
      }
    } catch (error) {
      console.error('Failed to fetch scores:', error);
    }
  };

  const handleSaveScore = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/rubric/scores`, {
        date: today,
        ...todayScore
      });
      toast.success('Score saved!');
      fetchScores();
    } catch (error) {
      toast.error('Failed to save score');
    } finally {
      setSaving(false);
    }
  };

  const totalScore = Object.values(todayScore).reduce((a, b) => a + b, 0);
  const avgScore = scores.length > 0
    ? scores.reduce((sum, s) => sum + s.total_score, 0) / scores.length
    : 0;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-2">Progress & Rubrics</h1>
        <p className="text-lg text-muted-foreground font-['IBM_Plex_Sans']">
          Track your daily scores and improvement
        </p>
      </div>

      {/* Today's Score Input */}
      <div className="mb-8 bg-card border border-border p-8 rounded-sm">
        <h2 className="text-2xl font-bold tracking-tight mb-6">Score Today's Work</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6\">
          {/* Lede Score */}
          <div>
            <Label htmlFor="lede" className="uppercase tracking-widest text-xs font-medium mb-2 block">
              Lede (out of 10)
            </Label>
            <Input
              id="lede"
              data-testid="input-lede-score"
              type="number"
              min="0"
              max="10"
              value={todayScore.lede_score}
              onChange={(e) => setTodayScore({ ...todayScore, lede_score: parseInt(e.target.value) || 0 })}
              className="font-['IBM_Plex_Sans']"
            />
            <p className="text-xs text-muted-foreground mt-1 font-['IBM_Plex_Sans']">
              5Ws+H present (4) • ≤35 words (3) • No jargon (3)
            </p>
          </div>

          {/* Nut Graf Score */}
          <div>
            <Label htmlFor="nutgraf" className="uppercase tracking-widest text-xs font-medium mb-2 block">
              Nut Graf (out of 10)
            </Label>
            <Input
              id="nutgraf"
              data-testid="input-nutgraf-score"
              type="number"
              min="0"
              max="10"
              value={todayScore.nut_graf_score}
              onChange={(e) => setTodayScore({ ...todayScore, nut_graf_score: parseInt(e.target.value) || 0 })}
              className="font-['IBM_Plex_Sans']"
            />
            <p className="text-xs text-muted-foreground mt-1 font-['IBM_Plex_Sans']">
              Stakes/why-now (4) • Specific angle (3) • Smooth transition (3)
            </p>
          </div>

          {/* Style Score */}
          <div>
            <Label htmlFor="style" className="uppercase tracking-widest text-xs font-medium mb-2 block">
              Style & Precision (out of 10)
            </Label>
            <Input
              id="style"
              data-testid="input-style-score"
              type="number"
              min="0"
              max="10"
              value={todayScore.style_score}
              onChange={(e) => setTodayScore({ ...todayScore, style_score: parseInt(e.target.value) || 0 })}
              className="font-['IBM_Plex_Sans']"
            />
            <p className="text-xs text-muted-foreground mt-1 font-['IBM_Plex_Sans']">
              AP compliance (4) • Clear sentences (3) • Attribution (3)
            </p>
          </div>

          {/* Fact-Check Score */}
          <div>
            <Label htmlFor="factcheck" className="uppercase tracking-widest text-xs font-medium mb-2 block">
              Fact-Checking (out of 10)
            </Label>
            <Input
              id="factcheck"
              data-testid="input-factcheck-score"
              type="number"
              min="0"
              max="10"
              value={todayScore.fact_check_score}
              onChange={(e) => setTodayScore({ ...todayScore, fact_check_score: parseInt(e.target.value) || 0 })}
              className="font-['IBM_Plex_Sans']"
            />
            <p className="text-xs text-muted-foreground mt-1 font-['IBM_Plex_Sans']">
              Names/dates verified (3) • Quotes checked (3) • Claims supported (2) • Uncertainties noted (2)
            </p>
          </div>
        </div>

        {/* Total Display */}
        <div className="flex items-center justify-between p-6 bg-muted rounded-sm mb-6">
          <div>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1 font-['IBM_Plex_Sans']">
              Total Score
            </p>
            <p className="text-4xl font-bold tracking-tight">
              {totalScore}<span className="text-2xl text-muted-foreground\">/40</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
              {totalScore >= 32 ? '✓ Passing' : 'Target: 32+'}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={(totalScore / 40) * 100} className="w-32 h-2" />
              <span className="text-sm font-medium font-['IBM_Plex_Sans']">
                {Math.round((totalScore / 40) * 100)}%
              </span>
            </div>
          </div>
        </div>

        <Button
          data-testid="btn-save-score"
          onClick={handleSaveScore}
          disabled={saving}
          className="w-full uppercase tracking-wide font-medium"
        >
          {saving ? 'Saving...' : 'Save Score'}
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-card border border-border p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold tracking-tight uppercase tracking-widest font-['IBM_Plex_Sans']">
              Average Score
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight mb-1">
            {avgScore.toFixed(1)}<span className="text-2xl text-muted-foreground">/40</span>
          </p>
          <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">
            Based on {scores.length} scored sessions
          </p>
        </div>

        <div className="bg-card border border-border p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold tracking-tight uppercase tracking-widest font-['IBM_Plex_Sans']">
              Passing Rate
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight mb-1">
            {scores.length > 0
              ? Math.round((scores.filter(s => s.total_score >= 32).length / scores.length) * 100)
              : 0}%
          </p>
          <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">
            {scores.filter(s => s.total_score >= 32).length} of {scores.length} sessions at 32+
          </p>
        </div>
      </div>

      {/* Score History */}
      <div className="bg-card border border-border p-8 rounded-sm">
        <h2 className="text-2xl font-bold tracking-tight mb-6">Score History</h2>
        
        {scores.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 font-['IBM_Plex_Sans']">
            No scores recorded yet. Complete today's work and score it above!
          </p>
        ) : (
          <div className="space-y-3">
            {scores.map((score, index) => (
              <div
                key={index}
                data-testid={`score-history-${index}`}
                className="flex items-center justify-between p-4 bg-muted/50 rounded-sm"
              >
                <span className="font-medium font-['IBM_Plex_Sans']">{score.date}</span>
                <div className="flex gap-6 text-sm font-['IBM_Plex_Sans']">
                  <span className="text-muted-foreground">Lede: {score.lede_score}</span>
                  <span className="text-muted-foreground">Nut: {score.nut_graf_score}</span>
                  <span className="text-muted-foreground">Style: {score.style_score}</span>
                  <span className="text-muted-foreground">Fact: {score.fact_check_score}</span>
                  <span className={`font-bold ${score.total_score >= 32 ? 'text-primary' : ''}`}>
                    Total: {score.total_score}/40
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

